import React, { useState, useEffect } from 'react';
import { Coffee, Heart, Gift, ArrowRight, Check, Mail } from 'lucide-react';

export default function BirthdayCoffee() {
  const [step, setStep] = useState('landing'); // landing, form, sending, success, recipient, card, celebration
  const [formData, setFormData] = useState({
    senderName: '',
    recipientName: '',
    recipientEmail: '',
    amount: 10,
    message: ''
  });
  const [cardOpen, setCardOpen] = useState(false);

  // Auto-transition from success to recipient view after 3 seconds
  useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => {
        setStep('recipient');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const handleSubmit = () => {
    if (!formData.senderName || !formData.recipientName || !formData.recipientEmail || !formData.message) {
      alert('Please fill in all fields!');
      return;
    }
    setStep('sending');
    setTimeout(() => setStep('success'), 1500);
  };

  const handleOpenCard = () => {
    setStep('card');
    setTimeout(() => setCardOpen(true), 500);
  };

  const handleCardClick = () => {
    if (cardOpen) {
      setStep('celebration');
    }
  };

  // Landing Page
  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Coffee className="w-16 h-16 text-amber-700" />
              <h1 className="text-6xl font-bold text-amber-900">birthday.coffee</h1>
            </div>
            <p className="text-2xl text-amber-800 mb-8">
              Everyone has a birthday.<br />
              Everyone deserves coffee.
            </p>
            <p className="text-xl text-amber-700 max-w-2xl mx-auto mb-12">
              Send someone special a virtual coffee on their birthday. 
              Because sometimes you can't be there in person, but you can still make their day.
            </p>
            <button
              onClick={() => setStep('form')}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xl px-12 py-4 rounded-full font-semibold flex items-center gap-2 mx-auto transition-all transform hover:scale-105"
            >
              Send Birthday Coffee <ArrowRight className="w-6 h-6" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-20">
            <div className="text-center p-6">
              <Gift className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-amber-900 mb-2">Instant Delivery</h3>
              <p className="text-amber-700">They get their gift card immediately via email</p>
            </div>
            <div className="text-center p-6">
              <Heart className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-amber-900 mb-2">Personal Touch</h3>
              <p className="text-amber-700">Add your own heartfelt birthday message</p>
            </div>
            <div className="text-center p-6">
              <Coffee className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-amber-900 mb-2">Universal Joy</h3>
              <p className="text-amber-700">Good at thousands of coffee shops nationwide</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form Step
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 py-12 px-6">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <Coffee className="w-10 h-10 text-amber-700" />
            <h2 className="text-3xl font-bold text-amber-900">Send Birthday Coffee</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-amber-900 font-semibold mb-2">Your Name</label>
              <input
                type="text"
                value={formData.senderName}
                onChange={(e) => setFormData({...formData, senderName: e.target.value})}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-amber-900 font-semibold mb-2">Recipient's Name</label>
              <input
                type="text"
                value={formData.recipientName}
                onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none"
                placeholder="Jane Smith"
              />
            </div>

            <div>
              <label className="block text-amber-900 font-semibold mb-2">Recipient's Email</label>
              <input
                type="email"
                value={formData.recipientEmail}
                onChange={(e) => setFormData({...formData, recipientEmail: e.target.value})}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none"
                placeholder="jane@example.com"
              />
            </div>

            <div>
              <label className="block text-amber-900 font-semibold mb-2">Gift Amount</label>
              <div className="flex gap-3">
                {[5, 10, 15, 25].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setFormData({...formData, amount})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                      formData.amount === amount
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-amber-900 font-semibold mb-2">Your Birthday Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none h-32"
                placeholder="Happy Birthday! Wish I could celebrate with you in person. Enjoy a coffee on me! 🎉"
              />
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xl py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Send Birthday Coffee Gift 🎁
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sending Animation
  if (step === 'sending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-center">
          <Mail className="w-32 h-32 text-white mx-auto mb-6 animate-bounce" />
          <h2 className="text-4xl font-bold text-white mb-4">Sending your gift...</h2>
          <div className="flex gap-2 justify-center">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen (Sender view - 3 seconds)
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600 flex items-center justify-center px-6">
        <div className="text-center max-w-2xl">
          <div className="mb-8">
            <Check className="w-32 h-32 text-white mx-auto animate-bounce" />
          </div>
          
          <h2 className="text-6xl font-bold text-white mb-6">
            🎉 Sent! 🎉
          </h2>
          
          <p className="text-2xl text-white mb-4">
            Your birthday coffee gift has been delivered to
          </p>
          <p className="text-3xl font-bold text-white mb-8">
            {formData.recipientName}
          </p>

          <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
            <p className="text-xl text-white">
              Now let's see what they'll experience...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Recipient Email View
  if (step === 'recipient') {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Email header */}
          <div className="bg-white rounded-t-xl p-6 border-b">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                <Coffee className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">birthday.coffee</p>
                <p className="text-sm text-gray-600">gifts@birthday.coffee</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">To: {formData.recipientEmail}</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-4">
              🎂 {formData.senderName} sent you a birthday gift!
            </h3>
          </div>

          {/* Email body */}
          <div className="bg-white p-8">
            <p className="text-lg text-gray-700 mb-6">
              Hey {formData.recipientName}! 🎉
            </p>
            <p className="text-lg text-gray-700 mb-8">
              {formData.senderName} wanted to make your birthday special by treating you to coffee. Click below to open your gift card!
            </p>

            <div className="text-center mb-8">
              <button
                onClick={handleOpenCard}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xl px-12 py-5 rounded-full font-semibold transition-all transform hover:scale-105 inline-flex items-center gap-3"
              >
                <Gift className="w-6 h-6" />
                Open Your Gift Card
              </button>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
              <Coffee className="w-12 h-12 text-amber-600 mx-auto mb-3" />
              <p className="text-amber-900 font-semibold text-lg">
                ${formData.amount} Coffee Gift Card
              </p>
              <p className="text-amber-700 text-sm mt-2">
                Redeemable at thousands of coffee shops
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-b-xl p-6 text-center text-sm text-gray-600">
            Sent with ❤️ from birthday.coffee
          </div>
        </div>
      </div>
    );
  }

  // Animated Card Step (Recipient opens card)
  if (step === 'card') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-white text-2xl mb-8">
            Opening your birthday card...
          </p>
          
          <div 
            className="relative w-96 h-64 mx-auto cursor-pointer"
            onClick={handleCardClick}
            style={{ perspective: '1000px' }}
          >
            <div 
              className="absolute inset-0 transition-all duration-1000"
              style={{
                transformStyle: 'preserve-3d',
                transform: cardOpen ? 'rotateY(180deg)' : 'rotateY(0deg)'
              }}
            >
              {/* Front of card */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-2xl flex items-center justify-center border-4 border-amber-300"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <div className="text-center">
                  <Gift className="w-24 h-24 text-white mx-auto mb-4" />
                  <p className="text-white text-2xl font-bold">Click to Open!</p>
                </div>
              </div>

              {/* Back of card */}
              <div 
                className="absolute inset-0 bg-white rounded-2xl shadow-2xl p-8 border-4 border-amber-300"
                style={{ 
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)'
                }}
              >
                <div className="h-full flex flex-col items-center justify-center">
                  <Coffee className="w-16 h-16 text-amber-600 mb-4 animate-bounce" />
                  <h2 className="text-4xl font-bold text-amber-900 mb-4">
                    Happy Birthday!
                  </h2>
                  <p className="text-lg text-amber-800 mb-4 italic text-center">
                    "{formData.message}"
                  </p>
                  <p className="text-amber-700 font-semibold">
                    - {formData.senderName}
                  </p>
                  <div className="mt-6 text-2xl font-bold text-amber-600">
                    ${formData.amount} Coffee Gift
                  </div>
                </div>
              </div>
            </div>
          </div>

          {cardOpen && (
            <p className="text-white text-xl mt-8 animate-pulse">
              Click the card to redeem your gift!
            </p>
          )}
        </div>
      </div>
    );
  }

  // Final Celebration (Recipient's view)
  if (step === 'celebration') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-blue-400 to-purple-500 flex items-center justify-center px-6">
        <div className="text-center max-w-2xl">
          <div className="mb-8 animate-bounce">
            <Coffee className="w-32 h-32 text-white mx-auto" />
          </div>
          
          <h2 className="text-6xl font-bold text-white mb-6">
            ☕ Enjoy! ☕
          </h2>
          
          <p className="text-2xl text-white mb-8">
            Your ${formData.amount} gift card is ready to use!
          </p>

          <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8">
            <div className="bg-white rounded-xl p-6 mb-4">
              <p className="text-gray-800 text-lg font-bold mb-2">Your Gift Card Code:</p>
              <p className="text-3xl font-mono font-bold text-amber-600">COFFEE-{Math.random().toString(36).substring(2, 10).toUpperCase()}</p>
            </div>
            <p className="text-white text-lg">
              Show this code at any participating coffee shop to redeem your ${formData.amount} gift!
            </p>
          </div>

          <button
            onClick={() => {
              setStep('landing');
              setFormData({
                senderName: '',
                recipientName: '',
                recipientEmail: '',
                amount: 10,
                message: ''
              });
              setCardOpen(false);
            }}
            className="bg-white text-purple-600 text-xl px-12 py-4 rounded-full font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105"
          >
            Send Your Own Birthday Coffee
          </button>
        </div>
      </div>
    );
  }
}